import React, { useState, useRef } from 'react';
import { TextField, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './login.css';
import axios from 'axios';
import { API_BASE_URL } from './config';

const Login = ({ onLogin }) => {
  const UserRef = useRef(null);
  const PasswordRef = useRef(null);
  const PasswordConfirmRef = useRef(null);
  const EmailRef = useRef(null);
  const DepartmentRef = useRef(null); // 新增 Department Ref

  const [activePage, setActivePage] = useState('login-page');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [signupUsername, setSignupUsername] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupDepartment, setSignupDepartment] = useState(''); // 新增部門 state
  const [signupEmailError, setSignupEmailError] = useState(false);
  const [signupPasswordError, setSignupPasswordError] = useState(false);
  const [signupUsernameError, setSignupUsernameError] = useState(false);
  const [signupPasswordConfirmError, setSignupPasswordConfirmError] = useState(false);
  const [signupDepartmentError, setSignupDepartmentError] = useState(false); // 新增部門錯誤 state
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [loginUsernameError, setLoginUsernameError] = useState(false);
  const [loginPasswordError, setLoginPasswordError] = useState(false);
  const [feedbackSeverity, setFeedbackSeverity] = useState('error');
  const [isRegistering, setIsRegistering] = useState(false);
  const navigate = useNavigate();

  const handleToggle = (pageToOpen) => {
    setActivePage(pageToOpen);
  };

  //登入 輸入框 錯誤判斷
  const handleLoginClick = async () => {
    console.log('Login attempt to:', `${API_BASE_URL}/login`);
    let hasError = false;

    if (!username) {
      setLoginUsernameError(true);
      hasError = true;
    }

    if (!password) {
      setLoginPasswordError(true);
      hasError = true;
    }

    //有錯誤時直接阻止提交
    if (hasError) return;

    try {
      const response = await axios.post(`${API_BASE_URL}/login`, { username, password });
      if (response.data.message === 'Login successful') {
        const { department, role } = response.data;
        
        onLogin({
          username: username,
          department: department || 'Unknown',
          role: role || 'User'
        });
        navigate('/machine-management');
      } else {
        setFeedbackSeverity('error');
        setErrorMessage('登入失敗，帳號或密碼錯誤');
        setForgotPasswordMessage('');
        setShowError(true);
      }
    } catch (error) { 
      setFeedbackSeverity('error');
      setErrorMessage('登入失敗，帳號或密碼錯誤');
      setForgotPasswordMessage('');
      setShowError(true);
    }
  };

  //註冊 輸入框 錯誤判斷
  const handleRegisterClick = async () => {
    console.log('Register attempt to:', `${API_BASE_URL}/register`);
    if (isRegistering) return;

    const normalizedUsername = signupUsername.trim();
    const normalizedEmail = signupEmail.trim().toLowerCase();
    const normalizedDepartment = signupDepartment.trim();
    let hasError = false;

    if (!normalizedUsername) {
      setSignupUsernameError(true);
      hasError = true;
    }

    if (!signupPassword) {
      setSignupPasswordError(true);
      hasError = true;
    }

    if (!signupPasswordConfirm) {
      setSignupPasswordConfirmError(true);
      hasError = true;
    }

    if (!normalizedEmail) {
      setSignupEmailError(true);
      hasError = true;
    }

    if (!normalizedDepartment) { // 檢查部門是否填寫
      setSignupDepartmentError(true);
      hasError = true;
    }

    //檢查密碼與確認密碼是否相同
    if (signupPassword !== signupPasswordConfirm) {
      setSignupPasswordError(true);
      setSignupPasswordConfirmError(true);
      hasError = true;
    }

    //檢查電子信箱格式
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setSignupEmailError(true);
      hasError = true;
    }

    //有錯誤時直接阻止提交
    if (hasError) return;

    setIsRegistering(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/register`, {
        username: normalizedUsername,
        password: signupPassword,
        email: normalizedEmail,
        department: normalizedDepartment, // 傳送部門資訊
      });

      if (response.data.message === 'User registered successfully') {
        setFeedbackSeverity('success');
        setErrorMessage('註冊成功');
        setForgotPasswordMessage('');
        setShowError(true);
        handleToggle('login-page');
      } else {
        setFeedbackSeverity('error');
        setErrorMessage('註冊失敗');
        setForgotPasswordMessage('');
        setShowError(true);
      }
    } catch (error) {
      console.error(error);
      const responseData = error.response?.data;
      const serverMessage = typeof responseData === 'string'
        ? responseData.trim()
        : responseData?.message;
      const msg = serverMessage === '錯誤的要求'
        ? '註冊失敗，帳號或電子信箱可能已被使用，請確認後再試'
        : serverMessage || '註冊時發生錯誤，請稍後再試';

      if (responseData?.field === 'username') setSignupUsernameError(true);
      if (responseData?.field === 'email') setSignupEmailError(true);
      setFeedbackSeverity('error');
      setErrorMessage(msg);
      setForgotPasswordMessage('');
      setShowError(true);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleForgotPasswordClick = async () => {
    try {
      await axios.post(`${API_BASE_URL}/forgot-password`, { email: forgotPasswordEmail });
      setFeedbackSeverity('success');
      setErrorMessage('');
      setForgotPasswordMessage('我們已成功發送郵件，請檢查您的收件匣。如未見來信，請留意垃圾郵件夾。');
      setShowError(true);
    } catch (error) {
      setFeedbackSeverity('error');
      setErrorMessage('');
      setForgotPasswordMessage('傳送失敗，請輸入已綁定帳號的電子信箱');
      setShowError(true);
    }
  };

  return (
    <div className="responsive-container">
      <Snackbar
        open={showError}
        autoHideDuration={10000} //提示視窗消失時間 10 秒
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity={feedbackSeverity} sx={{ width: '100%', backgroundColor: 'rgba(255, 255, 255)' }}>
          {errorMessage || forgotPasswordMessage}
        </Alert>
      </Snackbar>

      <div id="login-page" className={`auth-container ${activePage === 'login-page' ? 'active' : 'hidden'}`}>
        <h2>機台資料庫系統</h2>
        <div className="form-group">
          <div className="input-group">
            <TextField
              label="帳號"
              variant="filled"
              sx={{
                width: '100%',
                '& .MuiFilledInput-root': { minHeight: '45px' }, // 輸入框最小高度
                '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' }, // 內間距：上左下右

                '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },

                '& input:-webkit-autofill': {
                  backgroundColor: 'transparent !important',
                  WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& input:-webkit-autofill:focus': {
                  backgroundColor: 'transparent !important',
                },

                // 浮動標籤(點擊輸入框，標題會變小) 位置
                '& .MuiInputLabel-root': { top: '0px' },
                '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' } // 左右、上下 標題位置
              }}

              id="Username"
              type="text"
              error={loginUsernameError}
              helperText={loginUsernameError ? '請輸入有效的帳號' : ''}

              onChange={(e) => {
                setUsername(e.target.value);
                setLoginUsernameError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); // 阻止瀏覽器執行預設行為
                  UserRef.current?.focus(); // 移動到下一個輸入框
                }
              }}
            />
          </div>

          <div className="input-group">
            <TextField
              label="密碼"
              variant="filled"
              sx={{
                width: '100%',
                '& .MuiFilledInput-root': { minHeight: '45px' }, // 輸入框最小高度
                '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' }, // 內間距：上左下右

                '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },

                '& input:-webkit-autofill': {
                  backgroundColor: 'transparent !important',
                  WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& input:-webkit-autofill:focus': {
                  backgroundColor: 'transparent !important',
                },

                // 浮動標籤(點擊輸入框，標題會變小) 位置
                '& .MuiInputLabel-root': { top: '0px' },
                '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' } // 左右、上下 標題位置
              }}

              id='Password'
              type='Password'
              error={loginPasswordError}
              helperText={loginPasswordError ? '請輸入有效的密碼' : ''}
              onChange={(e) => {
                setPassword(e.target.value);
                setLoginPasswordError(false);
              }}
              inputRef={UserRef} //Ref，接收上個輸入框的enter
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault(); // 阻止瀏覽器執行預設行為
                  handleLoginClick();
                }
              }}
            />
          </div>

          <div className='button-group'>
            <div className='button-sm button-left'>
              <button
                className="btn btn-login"
                onClick={handleLoginClick}
              >
                登入
              </button>
            </div>
            <div className='button-sm  button-right'>
              <button
                className="btn btn-signup"
                onClick={() => handleToggle('signup-page')}
              >
                註冊
              </button>
            </div>
          </div>

          <div
            className="additional-links toggle-view"
            onClick={() => handleToggle('forgot-password-page')}
          >
            忘記密碼
          </div>
        </div>
      </div>

      <div id="signup-page" className={`auth-container ${activePage === 'signup-page' ? 'active' : 'hidden'}`}>
        <div className="close-button">
          <div
            className="toggle-view"
            onClick={() => handleToggle('login-page')}
          >
            <i className="bi bi-x"></i>
          </div>
        </div>
        <h2>註冊新帳戶</h2>
        <div className="form-group">
          <div className="input-group">
            <TextField
              label="帳號"
              variant="filled"
              sx={{
                width: '100%',
                '& .MuiFilledInput-root': { minHeight: '45px' }, // 輸入框最小高度
                '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' }, // 內間距：上左下右

                '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },

                '& input:-webkit-autofill': {
                  backgroundColor: 'transparent !important',
                  WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& input:-webkit-autofill:focus': {
                  backgroundColor: 'transparent !important',
                },

                // 浮動標籤(點擊輸入框，標題會變小) 位置
                '& .MuiInputLabel-root': { top: '0px' },
                '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' } // 左右、上下 標題位置
              }}

              id='signup-username'
              type='text'
              error={signupUsernameError}
              helperText={signupUsernameError ? '請輸入有效的帳號' : ''}

              onChange={(e) => {
                setSignupUsername(e.target.value);
                setSignupUsernameError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); //阻止瀏覽器執行預設行為
                  PasswordRef.current?.focus();
                }
              }}
            />
          </div>

          <div className="input-group">
            <TextField
              label="密碼"
              variant="filled"
              sx={{
                width: '100%',
                '& .MuiFilledInput-root': { minHeight: '45px' }, // 輸入框最小高度
                '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' }, // 內間距：上左下右

                '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },

                '& input:-webkit-autofill': {
                  backgroundColor: 'transparent !important',
                  WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& input:-webkit-autofill:focus': {
                  backgroundColor: 'transparent !important',
                },

                // 浮動標籤(點擊輸入框，標題會變小) 位置
                '& .MuiInputLabel-root': { top: '0px' },
                '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' } // 左右、上下 標題位置
              }}

              id='signup-password'
              type='password'
              error={signupPasswordError}
              helperText={signupPasswordError ? '請輸入有效的密碼' : ''}
              onChange={(e) => {
                setSignupPassword(e.target.value);
                setSignupPasswordError(false);
              }}
              inputRef={PasswordRef} // Ref，接收上個輸入框的enter
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); //阻止瀏覽器執行預設行為
                  PasswordConfirmRef.current?.focus();
                }
              }}
            />
          </div>

          <div className="input-group">
            <TextField
              label="確認密碼"
              variant="filled"
              sx={{
                width: '100%',
                '& .MuiFilledInput-root': { minHeight: '45px' }, // 輸入框最小高度
                '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' }, // 內間距：上左下右

                '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },

                '& input:-webkit-autofill': {
                  backgroundColor: 'transparent !important',
                  WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& input:-webkit-autofill:focus': {
                  backgroundColor: 'transparent !important',
                },

                // 浮動標籤(點擊輸入框，標題會變小) 位置
                '& .MuiInputLabel-root': { top: '0px' },
                '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' } // 左右、上下 標題位置
              }}

              id='signup-password-confirm'
              type='password'
              error={signupPasswordConfirmError}
              helperText={signupPasswordConfirmError ? '請再次輸入密碼' : ''}
              onChange={(e) => {
                setSignupPasswordConfirm(e.target.value);
                setSignupPasswordConfirmError(false);
              }}
              inputRef={PasswordConfirmRef} // Ref，接收上個輸入框的enter
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); //阻止瀏覽器執行預設行為
                  EmailRef.current?.focus();
                }
              }}
            />
          </div>

          <div className="input-group">
            <TextField
              label="電子信箱"
              variant="filled"
              sx={{
                width: '100%',
                '& .MuiFilledInput-root': { minHeight: '45px' },
                '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' },
                '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                '& input:-webkit-autofill': {
                  backgroundColor: 'transparent !important',
                  WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& input:-webkit-autofill:focus': {
                  backgroundColor: 'transparent !important',
                },
                '& .MuiInputLabel-root': { top: '0px' },
                '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' }
              }}
              id='signup-email'
              type='email'
              error={signupEmailError}
              helperText={signupEmailError ? '請輸入有效的電子信箱' : ''}
              onChange={(e) => {
                setSignupEmail(e.target.value);
                setSignupEmailError(false);
              }}
              inputRef={EmailRef} // Ref
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault(); 
                  DepartmentRef.current?.focus(); // 移動到部門輸入框
                }
              }}
            />
          </div>

          <div className="input-group">
            <TextField
              label="公司"
              variant="filled"  
              sx={{
                width: '100%',
                '& .MuiFilledInput-root': { minHeight: '45px' }, // 輸入框最小高度
                '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' }, // 內間距：上左下右

                '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },

                '& input:-webkit-autofill': {
                  backgroundColor: 'transparent !important',
                  WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                  transition: 'background-color 5000s ease-in-out 0s',
                },
                '& input:-webkit-autofill:focus': {
                  backgroundColor: 'transparent !important',
                },

                // 浮動標籤(點擊輸入框，標題會變小) 位置
                '& .MuiInputLabel-root': { top: '0px' },
                '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' } // 左右、上下 標題位置
              }}

              id='signup-department'
              type='text'
              error={signupDepartmentError}
              helperText={signupDepartmentError ? '請輸入有效的部門代碼' : ''}
              onChange={(e) => {
                setSignupDepartment(e.target.value);
                setSignupDepartmentError(false);
              }}
              inputRef={DepartmentRef} // Ref，接收上個輸入框的enter

              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault(); //阻止瀏覽器執行預設行為
                  handleRegisterClick();
                }
              }}

            />
          </div>
          <div className='button-group'>
            <button className="btn btn-register" onClick={handleRegisterClick} disabled={isRegistering}>
              {isRegistering ? '註冊中...' : '註冊'}
            </button>
          </div>
        </div>
      </div>

      <div id="forgot-password-page" className={`auth-container ${activePage === 'forgot-password-page' ? 'active' : 'hidden'}`}>
        <div className="close-button">
          <div
            className="toggle-view"
            onClick={() => handleToggle('login-page')}
          >
            <i className="bi bi-x"></i>
          </div>
        </div>
        <h2>寄送驗證信</h2>
        <div className='move-down'>
          <div className="form-group">
            <div className="input-group">
              <TextField
                label="電子信箱"
                variant="filled"
                sx={{
                  width: '100%',
                  '& .MuiFilledInput-root': { minHeight: '45px' }, // 輸入框最小高度
                  '& .MuiFilledInput-input': { padding: '5px 10px 0 10px' }, // 內間距：上左下右

                  '& .MuiFilledInput-root:before': { borderBottom: 'none' },
                  '& .MuiFilledInput-root:hover:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },

                  '& input:-webkit-autofill': {
                    backgroundColor: 'transparent !important',
                    WebkitBoxShadow: '0 0 0px 1000px transparent inset',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                  '& input:-webkit-autofill:focus': {
                    backgroundColor: 'transparent !important',
                  },

                  // 浮動標籤(點擊輸入框，標題會變小) 位置
                  '& .MuiInputLabel-root': { top: '0px' },
                  '& .MuiInputLabel-shrink': { transform: 'translate(10px, 2px) scale(0.7)' } // 左右、上下 標題位置
                }}

                id='forgot-email'
                type='email'
                error={signupEmailError}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
              />
            </div>
            <button className="btn btn-reset-password" onClick={handleForgotPasswordClick}>傳送連結</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
